import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/site/Reveal";
import { PHONE_TEL, SERVICES, TAGLINE } from "@/lib/site";

const HeroPhoto = ({ y }) => (
  <motion.div
    initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 28px 28px)" }}
    animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 28px 28px)" }}
    transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
    className="relative h-[420px] sm:h-[520px] lg:h-[600px] overflow-hidden"
    data-testid="hero-photo"
  >
    <motion.img
      src="/images/hero-painter.webp"
      alt="Decorator painting an interior wall with a roller – handyman and home improvement services in Oxfordshire"
      style={{ y, scale: 1.18 }}
      fetchpriority="high"
      className="absolute inset-0 h-full w-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/50 via-transparent to-transparent" />
  </motion.div>
);

const TaglineCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
    className="absolute -left-4 sm:-left-10 bottom-8 max-w-[240px] rounded-2xl bg-white p-5 text-[var(--navy)] shadow-2xl"
  >
    <p className="eyebrow text-[var(--green)]">Our promise</p>
    <p className="mt-2 font-accent text-2xl leading-tight">{TAGLINE}</p>
  </motion.div>
);

export const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section ref={ref} className="relative bg-[var(--navy)] text-white grain overflow-hidden" data-testid="home-hero">
      <div className="absolute left-[58%] top-0 bottom-0 w-px bg-white/5 hidden lg:block" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-14 pb-16 md:pt-20 lg:pt-24 lg:pb-24 grid gap-14 lg:grid-cols-12 lg:items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <Reveal><p className="eyebrow text-[var(--green-bright)] mb-7">Property maintenance · Oxfordshire</p></Reveal>
          <MaskedLines
            testId="hero-headline"
            className="font-display text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5.2rem] font-bold tracking-[-0.03em]"
            lines={[
              "Looking After",
              "Your Home Has",
              <>Never Been <span className="font-accent font-normal text-[var(--green-bright)] tracking-normal">Easier.</span></>,
            ]}
          />
          <Reveal delay={0.55}>
            <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-slate-300" data-testid="hero-subtext">
              From home improvements and garden maintenance to professional cleaning, we provide reliable property
              services throughout Oxfordshire.
            </p>
          </Reveal>
          <Reveal delay={0.7} className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to="/quote" data-testid="hero-get-free-quote-button"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-[var(--green)] px-8 py-4 font-semibold tracking-wide text-white transition-[background-color,transform] duration-300 hover:bg-[var(--green-hover)] hover:-translate-y-0.5">
              GET A FREE QUOTE <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
            <a href={PHONE_TEL} data-testid="hero-call-us-today-button"
              className="inline-flex items-center justify-center gap-3 rounded-full border border-white/30 px-8 py-4 font-semibold tracking-wide text-white transition-colors duration-300 hover:bg-white hover:text-[var(--navy)]">
              <Phone className="h-5 w-5" /> CALL US TODAY
            </a>
          </Reveal>
        </motion.div>
        <div className="lg:col-span-5 relative">
          <HeroPhoto y={y} />
          <TaglineCard />
        </div>
      </div>
      <HeroServiceStrip />
    </section>
  );
};

const HeroServiceStrip = () => (
  <div className="relative border-t border-white/10">
    <div className="mx-auto max-w-7xl grid sm:grid-cols-3">
      {SERVICES.map((s, i) => (
        <Reveal key={s.slug} delay={0.9 + i * 0.1}>
          <Link to={`/services#${s.slug}`} data-testid={`hero-service-link-${s.slug}`}
            className={`group flex items-center justify-between gap-4 px-6 lg:px-10 py-6 transition-colors duration-300 hover:bg-white/5 ${i > 0 ? "border-t sm:border-t-0 sm:border-l border-white/10" : ""}`}>
            <span className="flex items-baseline gap-4">
              <span className="font-display text-sm text-[var(--green-bright)]">{s.number}</span>
              <span className="font-display text-lg font-semibold">{s.title}</span>
            </span>
            <ArrowUpRight className="h-5 w-5 text-slate-400 transition-[transform,color] duration-300 group-hover:rotate-45 group-hover:text-white" />
          </Link>
        </Reveal>
      ))}
    </div>
  </div>
);
